import { Injectable } from '@angular/core';
import { firstValueFrom, from, lastValueFrom } from 'rxjs';
import { AvailableDeviceTypeDTO } from 'src/app/model/backend';
import { IProject } from 'src/app/model/user-management/project';
import { IReassignable, ITask, ITaskInputType } from 'src/app/model/thingie/task';
import { IThingie } from 'src/app/model/thingie/thingie';
import {
  IApplicationTemplate,
  IApplicationTemplateMetadata
} from 'src/app/model/thingie/templates';
import { ICreationContextAppTemplateTaskAmbiguityInfo } from 'src/app/model/thingie-creation-wizard/thingie-creation-context';
import { ISetupCopilotWizardPrefill, WizardStep } from 'src/app/model/thingie-creation-wizard/wizard-options';
import {
  ActionProposal,
  ChatHiddenContext,
  OpenWizardAtStepActionProposal,
  SetupCopilotBlockingTask,
  SetupCopilotContext,
  SetupCopilotTaskConfigurationSession,
  SetupExperimentDraft,
  SetupMeasurementGoal,
  SetupOrganismClass,
  SetupPendingQuestion,
  SetupProjectOption,
  SetupTemplateRecommendation,
  SetupTemplateTaskSlot
} from 'src/app/services/chat/chat.service';
import { deepCopySync } from 'src/app/utility/deep-copy';
import { extractTemplateChipsFromName } from 'src/app/utility/templates/extract-template-name-chips';
import { ExperimentCreationValidator } from 'src/app/utility/experiment-creation-validator';
import { createEmptyThingieTask } from 'src/app/utility/default-object-creator';
import { LicensingService } from '../licensing/licensing.service';
import { LogService } from '../loggers/logger.service';
import { ProjectService } from '../project/project.service';
import { DeviceService } from '../device/device.service';
import { TemplateService } from '../templates/template.service';
import { ThingieCreationHelperService } from '../thingie-creation-helper/thingie-creation-helper.service';
import { ThingieService } from '../thingie/thingie-service.service';
import { UserService } from '../user/user.service';

interface SetupCopilotTemplateCandidate {
  recommendation: SetupTemplateRecommendation;
  score: number;
  matchedTemplateHintCount: number;
  matchedGoalCount: number;
  missingGoalCount: number;
  extraGoalCount: number;
}

export interface SetupCopilotChatResult {
  handled: boolean;
  hiddenContext?: ChatHiddenContext;
  assistantMessage?: string;
  actionProposals?: ActionProposal[];
}

export interface SetupCopilotExecutionResult {
  ok: boolean;
  createdThingies?: IThingie[];
  errors?: string[];
}

type SetupFacts = {
  organismName?: string;
  organismClass?: SetupOrganismClass;
  measurementGoals?: SetupMeasurementGoal[];
  templateHints?: string[];
  replicateCount?: number;
  immediateCreateRequested?: boolean;
  recommendationOnly?: boolean;
  perReplicateOverridesRequested?: boolean;
  experimentName?: string;
  projectId?: string;
  projectTitle?: string;
};

const MICROBIAL_PATTERNS = [
  /e\.?\s*coli/i,
  /ecoli/i,
  /bacillus/i,
  /pichia/i,
  /yeast/i,
  /saccharomyces/i,
  /microbial/i,
  /bacteria/i,
  /bacterial/i,
  /fungal/i
];

const CELL_CULTURE_PATTERNS = [
  /\bcho\b/i,
  /\bhek\b/i,
  /vero/i,
  /sf9/i,
  /cell culture/i,
  /mammalian/i,
  /insect cell/i,
  /stem cell/i,
  /hybridoma/i
];

const MEASUREMENT_GOAL_PATTERNS: { goal: SetupMeasurementGoal; patterns: RegExp[] }[] = [
  { goal: 'growth', patterns: [/growth/i, /biomass/i, /backscatter/i, /cell density/i] },
  { goal: 'do', patterns: [/\bdo\b/i, /dissolved oxygen/i] },
  { goal: 'fluorescence', patterns: [/fluorescence/i, /\bgfp\b/i, /\brfp\b/i] },
  { goal: 'feeding', patterns: [/feeding/i, /fed[- ]?batch/i, /\blis\b/i, /\bfeed\b/i] },
  { goal: 'offline_biomass', patterns: [/offline biomass/i, /offline/i] },
  { goal: 'otr', patterns: [/\botr\b/i, /oxygen transfer/i] }
];

interface TemplateTextPattern {
  term: string;
  wholeWord: boolean;
}

const SETUP_TEMPLATE_HINT_PATTERNS: {
  hint: string;
  displayName: string;
  messagePatterns: RegExp[];
  templatePatterns: TemplateTextPattern[];
}[] = [
  {
    hint: 'cgq',
    displayName: 'CGQ',
    messagePatterns: [/\bcgq\b/i, /cell growth quantifier/i],
    templatePatterns: [{ term: 'cgq', wholeWord: true }, { term: 'aquilacgq', wholeWord: false }]
  },
  {
    hint: 'mps',
    displayName: 'MPS',
    messagePatterns: [/\bmps\b/i],
    templatePatterns: [{ term: 'mps', wholeWord: true }, { term: 'mpsfs', wholeWord: false }]
  },
  {
    hint: 'lis',
    displayName: 'LIS',
    messagePatterns: [/\blis\b/i],
    templatePatterns: [{ term: 'lis', wholeWord: true }]
  },
  {
    hint: 'bior_pro',
    displayName: 'BioR Pro',
    messagePatterns: [/\bbior pro\b/i, /\bbioreactor pro\b/i],
    templatePatterns: [{ term: 'bior pro', wholeWord: true }, { term: 'bioreactor pro', wholeWord: true }]
  },
  {
    hint: 'bior',
    displayName: 'BioR',
    messagePatterns: [/\bbior\b/i],
    templatePatterns: [{ term: 'bior', wholeWord: true }, { term: 'bioreactor', wholeWord: true }]
  }
];

const MAX_REPLICATE_COUNT = 96;
const MAX_SETUP_PROJECT_OPTIONS = 10;

const SETUP_RECOMMENDATION_INTENT_WORDS = [
  'create',
  'use',
  'first',
  'recommended',
  'project',
  'replicate',
  'replicates',
  'template',
  'yes'
];

const SETUP_RECOMMENDATION_INTENT_PHRASES = [
  'that one',
  'this one'
];

const SETUP_INTENT_WORDS = [
  'template',
  'setup',
  'application',
  'microbial'
];

const SETUP_INTENT_PHRASES = [
  'shake flask',
  'set up',
  'create experiment',
  'cell culture'
];

const GRAPH_ACTION_SIGNAL_WORDS = [
  'series',
  'graph',
  'chart',
  'dashboard',
  'tab',
  'plot',
  'axis',
  'rename',
  'duplicate'
];

const GRAPH_ACTION_SIGNAL_PHRASES = [
  'add series',
  'remove series',
  'replace series',
  'graph tab',
  'dashboard card',
  'add to graph',
  'to the graph',
  'to the dashboard',
  'from the graph',
  'from the dashboard'
];

const EXISTING_EXPERIMENT_ACTION_WORDS = [
  'annotate',
  'export'
];

const EXISTING_EXPERIMENT_ACTION_PHRASES = [
  'assign devices',
  'auto assign',
  'task configuration',
  'duplicate this experiment',
  'copy this experiment'
];

const SETUP_CREATE_REQUEST_WORDS = [
  'create',
  'setup',
  'build',
  'make'
];

const SETUP_CREATE_REQUEST_PHRASES = [
  'set up'
];

const SETUP_RECOMMENDATION_REQUEST_WORDS = [
  'recommend',
  'suggest'
];

const SETUP_RECOMMENDATION_REQUEST_PHRASES = [
  'which template'
];

const SETUP_PER_REPLICATE_OVERRIDE_PHRASES = [
  'per replicate',
  'different replicate',
  'different settings'
];

@Injectable({ providedIn: 'root' })
export class SetupCopilotService {
  public constructor(
    private deviceService: DeviceService,
    private licensingService: LicensingService,
    private log: LogService,
    private projectService: ProjectService,
    private templateService: TemplateService,
    private thingieCreationHelper: ThingieCreationHelperService,
    private thingieService: ThingieService,
    private userService: UserService
  ) {}

  public async handleMessage(
    message: string,
    hiddenContext?: ChatHiddenContext,
    options?: { forceIntent?: boolean; preferImmediateCreate?: boolean }
  ): Promise<SetupCopilotChatResult> {
    const existingContext = deepCopySync(hiddenContext?.setupCopilotContext ?? {});
    if (!options?.forceIntent && !this.isSetupIntent(message, existingContext, hiddenContext)) {
      return { handled: false, hiddenContext };
    }

    const nextContext = await this.buildContextFromMessage(message, existingContext);
    if (options?.preferImmediateCreate) {
      nextContext.immediateCreateRequested = true;
      nextContext.recommendationOnly = undefined;
    }
    const nextHiddenContext: ChatHiddenContext = {
      ...(hiddenContext ?? {}),
      setupCopilotContext: nextContext
    };

    if (nextContext.taskConfigurationSession) {
      return this.continueTaskConfiguration(nextContext, hiddenContext);
    }

    if (!nextContext.organismClass) {
      nextContext.pendingQuestion = 'organism_class';
      return {
        handled: true,
        hiddenContext: nextHiddenContext,
        assistantMessage: 'Is this a microbial or a cell culture application? I can narrow the templates down once I know that.'
      };
    }

    if ((nextContext.measurementGoals?.length ?? 0) === 0) {
      nextContext.pendingQuestion = 'measurement_goal';
      return {
        handled: true,
        hiddenContext: nextHiddenContext,
        assistantMessage: 'What do you want to monitor in this shake flask run? For example growth, DO, fluorescence, feeding, offline biomass, or OTR.'
      };
    }

    const accessibleProjects = await this.getAccessibleEditorProjects();
    const currentProject = this.resolveProject(nextContext, message, accessibleProjects);
    if (currentProject) {
      nextContext.projectId = currentProject._id;
      nextContext.projectTitle = currentProject.title;
    }

    const candidates = await this.buildTemplateCandidates(nextContext);
    nextContext.recommendations = candidates.map(candidate => candidate.recommendation);
    if (candidates.length === 0) {
      nextContext.pendingQuestion = undefined;
      return {
        handled: true,
        hiddenContext: nextHiddenContext,
        assistantMessage: this.buildNoMatchesMessage(nextContext)
      };
    }

    if (nextContext.perReplicateOverridesRequested === true) {
      const wizardFallback = this.buildWizardFallbackAction(
        WizardStep.ReplicateConfiguration,
        nextContext,
        candidates[0].recommendation,
        'Open the wizard with this template and continue at replicate configuration.'
      );
      nextContext.pendingQuestion = undefined;
      return {
        handled: true,
        hiddenContext: nextHiddenContext,
        assistantMessage: [
          this.buildRecommendationMessage(nextContext, candidates),
          '',
          'I noticed you want per-replicate differences. I can recommend the best template here, but direct chat-based creation currently assumes a shared setup across replicates, so per-replicate overrides should still be adjusted in the wizard afterwards.'
        ].join('\n'),
        actionProposals: wizardFallback ? [wizardFallback] : undefined
      };
    }

    if (nextContext.immediateCreateRequested === true && currentProject === undefined && accessibleProjects.length > 1) {
      nextContext.pendingQuestion = 'project';
      return {
        handled: true,
        hiddenContext: nextHiddenContext,
        assistantMessage: this.buildProjectQuestion(accessibleProjects)
      };
    }

    if (nextContext.immediateCreateRequested === true && !nextContext.replicateCount) {
      nextContext.pendingQuestion = 'replicate_count';
      return {
        handled: true,
        hiddenContext: nextHiddenContext,
        assistantMessage: `I recommend \`${candidates[0].recommendation.templateName}\` so far. How many replicates do you want me to create?`
      };
    }

    if (nextContext.immediateCreateRequested === true && nextContext.replicateCount && currentProject && !nextContext.experimentName) {
      nextContext.pendingQuestion = 'experiment_name';
      return {
        handled: true,
        hiddenContext: nextHiddenContext,
        assistantMessage: 'What should I call the experiment?'
      };
    }

    if (nextContext.immediateCreateRequested === true && nextContext.replicateCount && currentProject) {
      const draftResult = await this.buildDraftForTopTemplate(nextContext, candidates[0].recommendation);
      if (!draftResult.ok) {
        return {
          handled: true,
          hiddenContext: nextHiddenContext,
          assistantMessage: draftResult.message,
          actionProposals: draftResult.actionProposals
        };
      }

      nextContext.pendingQuestion = undefined;
      nextContext.draft = draftResult.draft;
      return {
        handled: true,
        hiddenContext: nextHiddenContext,
        assistantMessage: this.buildDraftSummaryMessage(draftResult.draft),
        actionProposals: [this.buildCreateExperimentActionProposal(draftResult.draft)]
      };
    }

    nextContext.pendingQuestion = undefined;
    return {
      handled: true,
      hiddenContext: nextHiddenContext,
      assistantMessage: this.buildRecommendationMessage(nextContext, candidates)
    };
  }

  public async enrichHiddenContextForMessage(
    message: string,
    hiddenContext?: ChatHiddenContext
  ): Promise<ChatHiddenContext | undefined> {
    const existingContext = deepCopySync(hiddenContext?.setupCopilotContext ?? {});
    if (!this.shouldBuildSetupContextForLlm(message, existingContext, hiddenContext)) {
      return hiddenContext;
    }

    const nextContext = await this.buildContextFromMessage(message, existingContext);
    const accessibleProjects = await this.getAccessibleEditorProjects();
    const currentProject = this.resolveProject(nextContext, message, accessibleProjects);
    if (currentProject) {
      nextContext.projectId = currentProject._id;
      nextContext.projectTitle = currentProject.title;
    }
    nextContext.availableProjects = this.buildProjectOptions(accessibleProjects);

    if (this.hasTemplateRecommendationSignals(nextContext)) {
      const candidates = await this.buildTemplateCandidates(nextContext);
      nextContext.recommendations = candidates.length > 0
        ? candidates.map(candidate => candidate.recommendation)
        : undefined;
    }

    return {
      ...(hiddenContext ?? {}),
      setupCopilotContext: nextContext
    };
  }

  public async enrichFullSetupContextForLlm(
    message: string,
    hiddenContext?: ChatHiddenContext
  ): Promise<ChatHiddenContext | undefined> {
    const existingContext = deepCopySync(hiddenContext?.setupCopilotContext ?? {});
    const nextContext = await this.buildContextFromMessage(message, existingContext);

    const accessibleProjects = await this.getAccessibleEditorProjects();
    const currentProject = this.resolveProject(nextContext, message, accessibleProjects);
    if (currentProject) {
      nextContext.projectId = currentProject._id;
      nextContext.projectTitle = currentProject.title;
    }
    nextContext.availableProjects = this.buildProjectOptions(accessibleProjects);

    const candidates = await this.buildTemplateCandidatesForLlm(nextContext);
    if (candidates.length > 0) {
      nextContext.recommendations = candidates.map(candidate => candidate.recommendation);
    }

    return {
      ...(hiddenContext ?? {}),
      setupCopilotContext: nextContext
    };
  }

  private async buildTemplateCandidatesForLlm(
    context: SetupCopilotContext
  ): Promise<SetupCopilotTemplateCandidate[]> {
    const [availableDeviceTypes, licenses, templateMetadataList] = await Promise.all([
      firstValueFrom(this.deviceService.getAvailableDeviceTypes()),
      this.licensingService.getTotalSeatCounts(),
      firstValueFrom(this.templateService.getApplicationTemplateMetadataList())
    ]);

    const availableDeviceTypeSet = this.buildAvailableDeviceTypeSet(availableDeviceTypes);
    const candidates = templateMetadataList
      .filter((templateMetadata): templateMetadata is IApplicationTemplateMetadata & { _id: string } => !!templateMetadata._id)
      .flatMap(templateMetadata => {
        const candidate = this.toTemplateCandidate(
          templateMetadata,
          context,
          licenses,
          availableDeviceTypeSet
        );
        if (!candidate) {
          return [];
        }
        return [candidate];
      });
    const hintFilteredCandidates = this.filterTemplateCandidatesByRequestedHints(
      candidates,
      context.requestedTemplateHints ?? []
    );
    const scored = this.filterTemplateCandidates(hintFilteredCandidates, context.measurementGoals ?? [])
      .sort((left, right) => right.score - left.score || left.recommendation.templateName.localeCompare(right.recommendation.templateName))
      .slice(0, 10);

    const taskTemplateMetadata = await firstValueFrom(this.templateService.getTaskTemplateMetadataList());
    for (const candidate of scored) {
      candidate.recommendation.taskSlots = this.detectTaskSlots(candidate.recommendation, templateMetadataList, taskTemplateMetadata);
    }

    return scored;
  }

  private detectTaskSlots(
    recommendation: SetupTemplateRecommendation,
    templateMetadataList: IApplicationTemplateMetadata[],
    taskTemplateMetadata: { _id?: string; name: string; usedFor: string[] }[]
  ): SetupTemplateTaskSlot[] | undefined {
    const templateMetadata = templateMetadataList.find(t => t._id === recommendation.templateId);
    if (!templateMetadata) {
      return undefined;
    }

    const slots: SetupTemplateTaskSlot[] = [];
    const processMetadataList = templateMetadata.replicateMetadata[0]?.processMetadata ?? [];
    for (let processIdx = 0; processIdx < processMetadataList.length; processIdx += 1) {
      const processMetadata = processMetadataList[processIdx];
      for (let taskIdx = 0; taskIdx < processMetadata.taskTemplateMetadata.length; taskIdx += 1) {
        const taskMeta = processMetadata.taskTemplateMetadata[taskIdx];
        const usedForValues = taskMeta.usedFor ?? [];
        for (const usedFor of usedForValues) {
          const compatibles = taskTemplateMetadata.filter(candidate => candidate.usedFor.includes(usedFor));
          if (compatibles.length > 1) {
            slots.push({
              processIdx,
              taskIdx,
              slotName: taskMeta.name,
              usedFor,
              options: compatibles.map(c => ({
                taskTemplateId: c._id ?? '',
                taskTemplateName: c.name
              })).filter(o => o.taskTemplateId)
            });
          }
        }
      }
    }

    return slots.length > 0 ? slots : undefined;
  }

  public syncTaskConfigurationContext(context: SetupCopilotContext): SetupCopilotContext {
    const nextContext = deepCopySync(context);
    if (!nextContext.taskConfigurationSession) {
      return nextContext;
    }

    this.syncTaskConfigurationSession(nextContext.taskConfigurationSession);
    return nextContext;
  }

  public continueTaskConfiguration(
    context: SetupCopilotContext,
    hiddenContext?: ChatHiddenContext
  ): SetupCopilotChatResult {
    const nextContext = this.syncTaskConfigurationContext(context);
    const nextHiddenContext: ChatHiddenContext = {
      ...(hiddenContext ?? {}),
      setupCopilotContext: nextContext
    };
    const taskConfigurationSession = nextContext.taskConfigurationSession;
    if (!taskConfigurationSession) {
      return {
        handled: true,
        hiddenContext: nextHiddenContext,
        assistantMessage: 'I could not resume task configuration from the current Copilot state.'
      };
    }

    if (taskConfigurationSession.blockingTasks.length === 0 && nextContext.draft) {
      nextContext.pendingQuestion = undefined;
      return {
        handled: true,
        hiddenContext: nextHiddenContext,
        assistantMessage: this.buildDraftSummaryMessage(nextContext.draft),
        actionProposals: [this.buildCreateExperimentActionProposal(nextContext.draft)]
      };
    }

    taskConfigurationSession.focusedTask = this.resolveNextFocusedTask(taskConfigurationSession);
    nextContext.pendingQuestion = undefined;
    return {
      handled: true,
      hiddenContext: nextHiddenContext,
      assistantMessage: this.buildTaskConfigurationMessage(taskConfigurationSession)
    };
  }

  public async createExperimentFromDraft(
    draft: SetupExperimentDraft,
    context?: SetupCopilotContext
  ): Promise<SetupCopilotExecutionResult> {
    try {
      const validatedDraft = await this.validateDraftForCreation(draft);
      if (!validatedDraft.ok) {
        return {
          ok: false,
          errors: validatedDraft.errors
        };
      }

      const normalizedDraft = validatedDraft.draft;
      const appTemplate = await this.templateService.getApplicationTemplateById(normalizedDraft.templateId);
      if (!appTemplate) {
        return {
          ok: false,
          errors: ['The selected application template could not be loaded.']
        };
      }

      const preparedTemplate = await this.prepareTemplate(deepCopySync(appTemplate));
      if (preparedTemplate.ambiguities.length > 0) {
        return {
          ok: false,
          errors: ['The selected template still requires manual task selection in the wizard.']
        };
      }

      const taskConfigurationSession = context?.taskConfigurationSession
        ? this.syncTaskConfigurationContext(context).taskConfigurationSession
        : undefined;
      if (taskConfigurationSession && taskConfigurationSession.blockingTasks.length > 0) {
        return {
          ok: false,
          errors: ['Please complete the required task configuration before creating the experiment.']
        };
      }

      const configuredThingies = taskConfigurationSession
        ? this.buildConfiguredThingiesForCreation(taskConfigurationSession, normalizedDraft)
        : undefined;
      const templateThingie = configuredThingies?.templateThingie ?? this.buildTemplateThingieForDraft(
        preparedTemplate.template,
        normalizedDraft
      );
      const draftThingies = configuredThingies?.thingies ?? this.buildDraftThingies(preparedTemplate.template, normalizedDraft);
      const createdThingies = await this.thingieCreationHelper.preProcessAndCreate(
        draftThingies,
        templateThingie
      );

      return {
        ok: true,
        createdThingies
      };
    } catch (error) {
      this.log.error('Failed to create experiment from Setup Copilot draft', error);
      return {
        ok: false,
        errors: [error instanceof Error ? error.message : 'Failed to create the experiment from chat.']
      };
    }
  }

  public async buildWizardPrefill(
    prefill: ISetupCopilotWizardPrefill
  ): Promise<{
      applicationTemplate: IApplicationTemplate;
      thingieTemplate: IThingie;
      thingies: IThingie[];
      taskAmbiguities: ICreationContextAppTemplateTaskAmbiguityInfo[];
      teamID: string;
    }> {
    const applicationTemplate = await this.templateService.getApplicationTemplateById(prefill.templateId);
    if (!applicationTemplate) {
      throw new Error('The selected application template could not be loaded.');
    }

    const preparedTemplate = await this.prepareTemplate(deepCopySync(applicationTemplate));
    const replicateCount = this.normalizeReplicateCount(prefill.replicateCount ?? preparedTemplate.template.thingies?.length ?? 1);
    if (!replicateCount) {
      throw new Error(`Replicate count must be between 1 and ${MAX_REPLICATE_COUNT}.`);
    }

    const preferredExperimentName = prefill.experimentName?.trim()
      || prefill.organismName?.trim()
      || preparedTemplate.template.templateThingie.baseName?.trim()
      || preparedTemplate.template.name.trim();
    const experimentName = await this.resolveAvailableExperimentName(preferredExperimentName);
    const draft: SetupExperimentDraft = {
      templateId: prefill.templateId,
      templateName: prefill.templateName?.trim() || preparedTemplate.template.name,
      experimentName,
      projectId: prefill.projectId?.trim() || '',
      projectTitle: prefill.projectTitle?.trim() || '',
      replicateCount,
      organismName: prefill.organismName?.trim() || undefined,
      organismClass: prefill.organismClass === 'cell_culture' ? 'cell_culture' : 'microbial',
      measurementGoals: this.normalizeMeasurementGoals(prefill.measurementGoals),
      requiredDeviceTypes: []
    };
    const thingieTemplate = deepCopySync(preparedTemplate.template.templateThingie);
    thingieTemplate.baseName = experimentName;
    thingieTemplate.project = draft.projectId;
    thingieTemplate.createdFromTemplateId = draft.templateId;

    const thingies = this.buildDraftThingies(preparedTemplate.template, draft);
    const teamID = await this.resolveTeamIdForProject(draft.projectId);
    return {
      applicationTemplate: preparedTemplate.template,
      thingieTemplate,
      thingies,
      taskAmbiguities: preparedTemplate.ambiguities,
      teamID
    };
  }

  private async buildContextFromMessage(
    message: string,
    existingContext: SetupCopilotContext
  ): Promise<SetupCopilotContext> {
    const nextContext: SetupCopilotContext = {
      ...existingContext
    };
    const inferredFacts = await this.extractFacts(message, existingContext.pendingQuestion);
    if (inferredFacts.organismName) {
      nextContext.organismName = inferredFacts.organismName;
    }
    if (inferredFacts.organismClass) {
      nextContext.organismClass = inferredFacts.organismClass;
    }
    if ((inferredFacts.measurementGoals?.length ?? 0) > 0) {
      nextContext.measurementGoals = inferredFacts.measurementGoals;
    }
    if ((inferredFacts.templateHints?.length ?? 0) > 0) {
      nextContext.requestedTemplateHints = inferredFacts.templateHints;
    }
    if (inferredFacts.replicateCount) {
      nextContext.replicateCount = inferredFacts.replicateCount;
    }
    if (inferredFacts.experimentName) {
      nextContext.experimentName = inferredFacts.experimentName;
    }
    if (inferredFacts.immediateCreateRequested !== undefined) {
      nextContext.immediateCreateRequested = inferredFacts.immediateCreateRequested;
    }
    if (inferredFacts.recommendationOnly !== undefined) {
      nextContext.recommendationOnly = inferredFacts.recommendationOnly;
    }
    if (inferredFacts.perReplicateOverridesRequested !== undefined) {
      nextContext.perReplicateOverridesRequested = inferredFacts.perReplicateOverridesRequested;
    }
    if (inferredFacts.projectId) {
      nextContext.projectId = inferredFacts.projectId;
      nextContext.projectTitle = inferredFacts.projectTitle;
    }

    if (!nextContext.organismClass && nextContext.organismName) {
      nextContext.organismClass = this.inferOrganismClass(nextContext.organismName);
    }

    if (nextContext.pendingQuestion && this.isPendingQuestionResolved(nextContext.pendingQuestion, nextContext)) {
      nextContext.pendingQuestion = undefined;
    }

    return nextContext;
  }

  private async extractFacts(
    message: string,
    pendingQuestion?: SetupPendingQuestion
  ): Promise<SetupFacts> {
    const normalizedMessage = message.trim();
    const measurementGoals = this.extractMeasurementGoals(normalizedMessage);
    const experimentName = this.extractExperimentNameFromMessage(normalizedMessage, pendingQuestion);

    return {
      organismName: this.extractOrganismName(normalizedMessage),
      organismClass: this.inferOrganismClass(normalizedMessage),
      measurementGoals: measurementGoals.length > 0 ? measurementGoals : undefined,
      templateHints: this.extractRequestedTemplateHints(normalizedMessage),
      replicateCount: this.extractReplicateCount(normalizedMessage),
      immediateCreateRequested: this.containsAnySetupIntentTerm(
        normalizedMessage,
        SETUP_CREATE_REQUEST_WORDS,
        SETUP_CREATE_REQUEST_PHRASES
      ) ? true : undefined,
      recommendationOnly: this.containsAnySetupIntentTerm(
        normalizedMessage,
        SETUP_RECOMMENDATION_REQUEST_WORDS,
        SETUP_RECOMMENDATION_REQUEST_PHRASES
      ) ? true : undefined,
      perReplicateOverridesRequested: this.containsAnySetupIntentTerm(
        normalizedMessage,
        [],
        SETUP_PER_REPLICATE_OVERRIDE_PHRASES
      ) ? true : undefined,
      experimentName
    };
  }

  private async buildTemplateCandidates(
    context: SetupCopilotContext
  ): Promise<SetupCopilotTemplateCandidate[]> {
    const [availableDeviceTypes, licenses, templateMetadataList] = await Promise.all([
      firstValueFrom(this.deviceService.getAvailableDeviceTypes()),
      this.licensingService.getTotalSeatCounts(),
      firstValueFrom(this.templateService.getApplicationTemplateMetadataList())
    ]);

    const availableDeviceTypeSet = this.buildAvailableDeviceTypeSet(availableDeviceTypes);
    const candidates = templateMetadataList
      .filter((templateMetadata): templateMetadata is IApplicationTemplateMetadata & { _id: string } => !!templateMetadata._id)
      .flatMap(templateMetadata => {
        const candidate = this.toTemplateCandidate(
          templateMetadata,
          context,
          licenses,
          availableDeviceTypeSet
        );
        if (!candidate) {
          return [];
        }
        return [candidate];
      });
    const hintFilteredCandidates = this.filterTemplateCandidatesByRequestedHints(
      candidates,
      context.requestedTemplateHints ?? []
    );
    return this.filterTemplateCandidates(hintFilteredCandidates, context.measurementGoals ?? [])
      .sort((left, right) => right.score - left.score || left.recommendation.templateName.localeCompare(right.recommendation.templateName))
      .slice(0, 3);
  }

  private toTemplateCandidate(
    templateMetadata: IApplicationTemplateMetadata & { _id: string },
    context: SetupCopilotContext,
    licenses: { [licenseType: string]: number },
    availableDeviceTypeSet: Set<string>
  ): SetupCopilotTemplateCandidate | undefined {
    const organismClass = this.inferTemplateOrganismClass(templateMetadata.name);
    if (context.organismClass && organismClass && organismClass !== context.organismClass) {
      return undefined;
    }

    const taskTemplateMetadata = templateMetadata.replicateMetadata[0]?.processMetadata
      .flatMap(process => process.taskTemplateMetadata) ?? [];
    const hasActiveLicense = taskTemplateMetadata.every(task => task.licensing?.some(
      licenseChoice => licenseChoice.every(license => (licenses[license] ?? 0) > 0)
    ) ?? true);
    if (!hasActiveLicense) {
      return undefined;
    }

    const requiredDeviceTypes = [...new Set(taskTemplateMetadata
      .flatMap(task => task.deviceInputs?.map(input => input.deviceType) ?? [])
      .filter(Boolean))];
    if (requiredDeviceTypes.some(deviceType => !availableDeviceTypeSet.has(deviceType))) {
      return undefined;
    }

    const searchableText = this.buildTemplateSearchText(templateMetadata);
    const matchedTemplateHints = (context.requestedTemplateHints ?? [])
      .filter(hint => this.templateMatchesRequestedHint(searchableText, hint));
    const requestedGoals = context.measurementGoals ?? [];
    const templateGoals = this.extractTemplateGoals(searchableText);
    const matchedGoals = requestedGoals.filter(goal => templateGoals.includes(goal));
    if (requestedGoals.length > 0 && matchedGoals.length === 0) {
      return undefined;
    }
    const missingGoals = requestedGoals.filter(goal => !templateGoals.includes(goal));
    const extraGoals = templateGoals.filter(goal => !requestedGoals.includes(goal));

    const recommendation: SetupTemplateRecommendation = {
      templateId: templateMetadata._id,
      templateName: templateMetadata.name,
      organismClass,
      matchedGoals,
      requiredDeviceTypes,
      reasons: this.buildRecommendationReasons(
        templateMetadata,
        organismClass,
        matchedTemplateHints,
        matchedGoals,
        requiredDeviceTypes,
        extraGoals
      )
    };
    const candidate: SetupCopilotTemplateCandidate = {
      recommendation,
      score: 0,
      matchedTemplateHintCount: matchedTemplateHints.length,
      matchedGoalCount: matchedGoals.length,
      missingGoalCount: missingGoals.length,
      extraGoalCount: extraGoals.length
    };
    candidate.score = this.scoreTemplateCandidate(candidate, context);
    return candidate;
  }

  private filterTemplateCandidates(
    candidates: SetupCopilotTemplateCandidate[],
    requestedGoals: SetupMeasurementGoal[]
  ): SetupCopilotTemplateCandidate[] {
    if (requestedGoals.length === 0 || candidates.length <= 1) {
      return candidates;
    }

    const fullMatchCandidates = candidates.filter(candidate => candidate.missingGoalCount === 0);
    if (fullMatchCandidates.length > 0) {
      const lowestExtraGoalCount = Math.min(...fullMatchCandidates.map(candidate => candidate.extraGoalCount));
      return fullMatchCandidates.filter(candidate => candidate.extraGoalCount === lowestExtraGoalCount);
    }

    const highestMatchedGoalCount = Math.max(...candidates.map(candidate => candidate.matchedGoalCount));
    const closestCandidates = candidates.filter(candidate => candidate.matchedGoalCount === highestMatchedGoalCount);
    const lowestMissingGoalCount = Math.min(...closestCandidates.map(candidate => candidate.missingGoalCount));
    const lowestMissingCandidates = closestCandidates.filter(candidate => candidate.missingGoalCount === lowestMissingGoalCount);
    const lowestExtraGoalCount = Math.min(...lowestMissingCandidates.map(candidate => candidate.extraGoalCount));
    return lowestMissingCandidates.filter(candidate => candidate.extraGoalCount === lowestExtraGoalCount);
  }

  private filterTemplateCandidatesByRequestedHints(
    candidates: SetupCopilotTemplateCandidate[],
    requestedTemplateHints: string[]
  ): SetupCopilotTemplateCandidate[] {
    if (requestedTemplateHints.length === 0 || candidates.length <= 1) {
      return candidates;
    }

    const highestMatchedTemplateHintCount = Math.max(...candidates.map(candidate => candidate.matchedTemplateHintCount));
    if (highestMatchedTemplateHintCount === 0) {
      return candidates;
    }

    return candidates.filter(candidate => candidate.matchedTemplateHintCount === highestMatchedTemplateHintCount);
  }

  private buildRecommendationReasons(
    templateMetadata: IApplicationTemplateMetadata,
    organismClass: SetupOrganismClass | undefined,
    matchedTemplateHints: string[],
    matchedGoals: SetupMeasurementGoal[],
    requiredDeviceTypes: string[],
    extraGoals: SetupMeasurementGoal[]
  ): string[] {
    const reasons: string[] = [];
    if (organismClass === 'microbial') {
      reasons.push('Matches microbial workflows.');
    } else if (organismClass === 'cell_culture') {
      reasons.push('Matches cell culture workflows.');
    }
    if (matchedTemplateHints.length > 0) {
      reasons.push(
        `Matches your requested setup hint${matchedTemplateHints.length === 1 ? '' : 's'}: ${matchedTemplateHints
          .map(hint => this.formatTemplateHint(hint))
          .join(', ')}.`
      );
    }
    if (matchedGoals.length > 0) {
      reasons.push(`Fits your measurement goals: ${matchedGoals.map(goal => this.formatGoal(goal)).join(', ')}.`);
    }
    if (matchedGoals.length > 0 && extraGoals.length > 0) {
      reasons.push(`Also includes additional monitoring: ${extraGoals.map(goal => this.formatGoal(goal)).join(', ')}.`);
    }
    if (requiredDeviceTypes.length > 0) {
      reasons.push(`Needs supported device types: ${requiredDeviceTypes.join(', ')}.`);
    }
    if (reasons.length === 0) {
      reasons.push(`Matches the available metadata for ${templateMetadata.name}.`);
    }
    return reasons;
  }

  private scoreTemplateCandidate(
    candidate: SetupCopilotTemplateCandidate,
    context: SetupCopilotContext
  ): number {
    let score = 0;
    if (candidate.recommendation.organismClass && candidate.recommendation.organismClass === context.organismClass) {
      score += 10;
    }
    score += candidate.matchedTemplateHintCount * 18;
    if ((context.measurementGoals?.length ?? 0) === 0) {
      score += 1;
    } else if (candidate.missingGoalCount === 0) {
      score += 20;
    } else {
      score -= candidate.missingGoalCount * 8;
    }
    score += candidate.matchedGoalCount * 6;
    score -= candidate.extraGoalCount * 4;
    if (candidate.recommendation.requiredDeviceTypes.length > 0) {
      score += 2;
    }
    return score;
  }

  private async buildDraftForTopTemplate(
    context: SetupCopilotContext,
    recommendation: SetupTemplateRecommendation
  ): Promise<{ ok: true; draft: SetupExperimentDraft } | { ok: false; message: string; actionProposals?: ActionProposal[] }> {
    if (!context.projectId || !context.projectTitle || !context.replicateCount || !context.organismClass) {
      return {
        ok: false,
        message: 'I still need a project, organism class, and replicate count before I can draft the experiment.'
      };
    }

    const appTemplate = await this.templateService.getApplicationTemplateById(recommendation.templateId);
    if (!appTemplate) {
      return {
        ok: false,
        message: 'I could not load the recommended application template.'
      };
    }

    const preparedTemplate = await this.prepareTemplate(deepCopySync(appTemplate));
    if (preparedTemplate.ambiguities.length > 0) {
      context.taskConfigurationSession = undefined;
      const wizardFallback = this.buildWizardFallbackAction(
        WizardStep.ProcessStructure,
        context,
        recommendation,
        'Open the wizard and resolve the remaining task template choices.'
      );
      return {
        ok: false,
        message: `\`${recommendation.templateName}\` still needs manual task selection in the wizard, so I can recommend it but not create it directly from chat yet.`,
        actionProposals: wizardFallback ? [wizardFallback] : undefined
      };
    }

    const experimentName = await this.resolveAvailableExperimentName(this.buildExperimentName(context, appTemplate));
    const draft: SetupExperimentDraft = {
      templateId: recommendation.templateId,
      templateName: recommendation.templateName,
      experimentName,
      projectId: context.projectId,
      projectTitle: context.projectTitle,
      replicateCount: context.replicateCount,
      organismName: context.organismName,
      organismClass: context.organismClass,
      measurementGoals: context.measurementGoals ?? [],
      requiredDeviceTypes: recommendation.requiredDeviceTypes
    };
    context.draft = draft;

    const taskConfigurationSession = this.buildTaskConfigurationSession(preparedTemplate.template, draft);
    if (taskConfigurationSession.blockingTasks.length > 0) {
      context.taskConfigurationSession = taskConfigurationSession;
      return {
        ok: false,
        message: this.buildTaskConfigurationMessage(taskConfigurationSession)
      };
    }

    context.taskConfigurationSession = undefined;
    return {
      ok: true,
      draft
    };
  }

  private buildTemplateThingieForDraft(
    appTemplate: IApplicationTemplate,
    draft: SetupExperimentDraft
  ): IThingie {
    const templateThingie = deepCopySync(appTemplate.templateThingie);
    this.thingieCreationHelper.resetThingies([templateThingie]);
    this.thingieCreationHelper.resetConnectedDevices([templateThingie]);
    templateThingie.baseName = draft.experimentName;
    templateThingie.project = draft.projectId;
    templateThingie.createdFromTemplateId = draft.templateId;
    return templateThingie;
  }

  private buildDraftThingies(
    appTemplate: IApplicationTemplate,
    draft: SetupExperimentDraft
  ): IThingie[] {
    const templateThingie = deepCopySync(appTemplate.templateThingie);
    const baseReplicates = (appTemplate.thingies?.length ?? 0) > 0
      ? deepCopySync(appTemplate.thingies ?? [])
      : [deepCopySync(templateThingie)];

    while (baseReplicates.length < draft.replicateCount) {
      baseReplicates.push(deepCopySync(templateThingie));
    }

    const thingies = baseReplicates
      .slice(0, draft.replicateCount)
      .map(thingie => deepCopySync(thingie));

    this.thingieCreationHelper.resetThingies([templateThingie]);
    this.thingieCreationHelper.resetThingies(thingies);
    this.thingieCreationHelper.resetConnectedDevices(thingies);

    const replicateNames = this.buildReplicateNames(thingies.length);
    thingies.forEach((thingie, index) => {
      thingie.baseName = draft.experimentName;
      thingie.name = replicateNames[index];
      thingie.project = draft.projectId;
      thingie.createdFromTemplateId = draft.templateId;
    });

    return thingies;
  }

  private buildTaskConfigurationSession(
    appTemplate: IApplicationTemplate,
    draft: SetupExperimentDraft
  ): SetupCopilotTaskConfigurationSession {
    const taskConfigurationSession: SetupCopilotTaskConfigurationSession = {
      templateThingie: this.buildTemplateThingieForDraft(appTemplate, draft),
      thingies: this.buildDraftThingies(appTemplate, draft),
      blockingTasks: []
    };
    this.syncTaskConfigurationSession(taskConfigurationSession);
    return taskConfigurationSession;
  }

  private buildConfiguredThingiesForCreation(
    taskConfigurationSession: SetupCopilotTaskConfigurationSession,
    draft: SetupExperimentDraft
  ): { templateThingie: IThingie; thingies: IThingie[] } {
    const syncedSession = deepCopySync(taskConfigurationSession);
    this.syncTaskConfigurationSession(syncedSession);
    const templateThingie = deepCopySync(syncedSession.templateThingie);
    templateThingie.baseName = draft.experimentName;
    templateThingie.project = draft.projectId;
    templateThingie.createdFromTemplateId = draft.templateId;
    const thingies = deepCopySync(syncedSession.thingies);
    const replicateNames = this.buildReplicateNames(thingies.length);
    thingies.forEach((thingie, index) => {
      thingie.baseName = draft.experimentName;
      thingie.name = replicateNames[index];
      thingie.project = draft.projectId;
      thingie.createdFromTemplateId = draft.templateId;
    });
    return {
      templateThingie,
      thingies
    };
  }

  private async prepareTemplate(
    template: IApplicationTemplate
  ): Promise<{ template: IApplicationTemplate; ambiguities: ICreationContextAppTemplateTaskAmbiguityInfo[] }> {
    const ambiguities: ICreationContextAppTemplateTaskAmbiguityInfo[] = [];
    const taskTemplateMetadata = await firstValueFrom(this.templateService.getTaskTemplateMetadataList());
    const thingie = template.templateThingie;

    for (let processIdx = 0; processIdx < thingie.processes.length; processIdx += 1) {
      const process = thingie.processes[processIdx];
      if (!process.taskTemplates) {
        continue;
      }

      for (let taskIdx = 0; taskIdx < process.taskTemplates.length; taskIdx += 1) {
        const taskTemplateReference = process.taskTemplates[taskIdx];
        if (!taskTemplateReference || process.tasks[taskIdx]) {
          continue;
        }

        const compatibles = taskTemplateMetadata
          .filter(candidate => candidate.usedFor.includes(taskTemplateReference.usedFor));
        if (compatibles.length === 1) {
          const loadedTaskTemplate = await firstValueFrom(
            from(this.templateService.getTaskTemplateByMetaId(compatibles[0]._id ?? ''))
          );
          if (!loadedTaskTemplate) {
            continue;
          }

          const task = deepCopySync(loadedTaskTemplate.template.payload);
          task.createdFromTemplateId = compatibles[0]._id;
          task.name = taskTemplateReference.defaultName;
          process.tasks[taskIdx] = task;

          if (template.thingies) {
            template.thingies.forEach(replicate => {
              if (replicate.processes[processIdx]?.tasks[taskIdx]) {
                replicate.processes[processIdx].tasks[taskIdx].createdFromTemplateId = task.createdFromTemplateId;
              }
            });
          }
          continue;
        }

        process.tasks[taskIdx] = {
          ...createEmptyThingieTask(),
          name: taskTemplateReference.defaultName
        };
        if (compatibles.length > 1) {
          ambiguities.push({
            appTemplateId: template._id ?? '',
            processIdx,
            taskIdx,
            usedFor: taskTemplateReference.usedFor,
            numberOfOptions: compatibles.length
          });
        }
      }
    }

    return {
      template,
      ambiguities
    };
  }

  private buildRecommendationMessage(
    context: SetupCopilotContext,
    candidates: SetupCopilotTemplateCandidate[]
  ): string {
    const header = context.organismClass === 'cell_culture'
      ? 'I filtered the current templates down to cell culture-friendly options you can actually run.'
      : 'I filtered the current templates down to microbial options you can actually run.';
    const lines = candidates.map((candidate, index) => `${index + 1}. \`${candidate.recommendation.templateName}\` - ${candidate.recommendation.reasons.join(' ')}`);
    const footer = context.immediateCreateRequested
      ? 'If you want, tell me the replicate count and project and I can draft the experiment directly from chat.'
      : 'If you want, I can also draft the experiment directly from chat once you tell me the replicate count and project.';
    return [header, '', ...lines, '', footer].join('\n');
  }

  private buildDraftSummaryMessage(draft: SetupExperimentDraft): string {
    const goals = draft.measurementGoals.length > 0
      ? draft.measurementGoals.map(goal => this.formatGoal(goal)).join(', ')
      : 'general monitoring';
    return [
      `I drafted a new experiment from \`${draft.templateName}\`.`,
      '',
      `- Experiment name: \`${draft.experimentName}\``,
      `- Project: \`${draft.projectTitle}\``,
      `- Replicates: \`${draft.replicateCount}\``,
      `- Organism class: \`${draft.organismClass === 'cell_culture' ? 'Cell culture' : 'Microbial'}\``,
      `- Measurement goals: ${goals}`,
      `- Required device types: ${draft.requiredDeviceTypes.length > 0 ? draft.requiredDeviceTypes.join(', ') : 'None'}`
    ].join('\n');
  }

  private buildNoMatchesMessage(context: SetupCopilotContext): string {
    const formattedGoals = (context.measurementGoals ?? []).map(goal => this.formatGoal(goal)).join(', ');
    return `I could not find a template that matches your current setup constraints. I only recommend templates that have a valid license and supported device types. Current filters: organism class \`${context.organismClass ?? 'unknown'}\`${formattedGoals ? `, goals \`${formattedGoals}\`` : ''}.`;
  }

  private buildProjectQuestion(projects: IProject[]): string {
    const titles = projects.slice(0, 5)
      .map(project => `\`${project.title}\``)
      .join(', ');
    return `I can create the experiment, but I still need the target project. Which project should I use? Available editor projects include ${titles}.`;
  }

  private buildExperimentName(context: SetupCopilotContext, template: IApplicationTemplate): string {
    const explicitExperimentName = context.experimentName?.trim();
    if (explicitExperimentName) {
      return explicitExperimentName;
    }
    const draftName = context.draft?.experimentName?.trim();
    if (draftName) {
      return draftName;
    }
    const organismBasedName = context.organismName?.trim();
    if (organismBasedName) {
      return `${organismBasedName} setup`;
    }
    const templateBaseName = template.templateThingie.baseName?.trim();
    if (templateBaseName) {
      return templateBaseName;
    }
    return template.name.trim() || 'New experiment';
  }

  private async resolveAvailableExperimentName(preferredName: string): Promise<string> {
    const normalizedPreferredName = preferredName.trim();
    if (!(await this.isExperimentNameTaken(normalizedPreferredName))) {
      return normalizedPreferredName;
    }

    for (let suffix = 2; suffix <= 100; suffix += 1) {
      const candidate = `${normalizedPreferredName} (${suffix})`;
      if (!(await this.isExperimentNameTaken(candidate))) {
        return candidate;
      }
    }

    return `${normalizedPreferredName} (${Date.now()})`;
  }

  private async isExperimentNameTaken(experimentName: string): Promise<boolean> {
    const takenExperimentNames = await lastValueFrom(this.thingieService.checkForTakenNames([
      { name: experimentName }
    ]));
    return takenExperimentNames.includes(experimentName);
  }

  private resolveProject(
    context: SetupCopilotContext,
    message: string,
    accessibleProjects: IProject[]
  ): IProject | undefined {
    if (context.projectId) {
      return accessibleProjects.find(project => project._id === context.projectId);
    }

    const lowerCaseMessage = message.toLowerCase();
    const matchingProject = accessibleProjects.find(project => lowerCaseMessage.includes(project.title.toLowerCase()));
    if (matchingProject) {
      return matchingProject;
    }

    if (accessibleProjects.length === 1) {
      return accessibleProjects[0];
    }

    return undefined;
  }

  private async getAccessibleEditorProjects(): Promise<IProject[]> {
    const [currentUser, allProjects] = await Promise.all([
      this.userService.getCurrentUser(),
      firstValueFrom(this.projectService.getAllProjects())
    ]);
    const accessibleProjects = currentUser.admin
      ? allProjects
      : allProjects.filter(project => currentUser.projects[project._id] === 'EDITOR');
    return accessibleProjects.sort((left, right) => left.title.localeCompare(right.title));
  }

  private buildProjectOptions(projects: IProject[]): SetupProjectOption[] | undefined {
    const availableProjects = projects
      .slice(0, MAX_SETUP_PROJECT_OPTIONS)
      .map(project => ({
        projectId: project._id,
        projectTitle: project.title
      }))
      .filter(project => project.projectId && project.projectTitle);
    return availableProjects.length > 0 ? availableProjects : undefined;
  }

  private buildAvailableDeviceTypeSet(availableDeviceTypes: AvailableDeviceTypeDTO[]): Set<string> {
    const result = new Set<string>();
    availableDeviceTypes.forEach(deviceType => {
      result.add(deviceType.type);
      (deviceType.typeSelections ?? []).forEach(selection => {
        if (selection.type) {
          result.add(selection.type);
        }
        if (selection.deviceInputType) {
          result.add(selection.deviceInputType);
        }
      });
    });
    return result;
  }

  private isSetupIntent(message: string, context: SetupCopilotContext, hiddenContext?: ChatHiddenContext): boolean {
    if (context.taskConfigurationSession !== undefined) {
      return true;
    }
    if (context.pendingQuestion !== undefined) {
      return true;
    }
    if ((context.recommendations?.length ?? 0) > 0
      && this.containsAnySetupIntentTerm(
        message,
        SETUP_RECOMMENDATION_INTENT_WORDS,
        SETUP_RECOMMENDATION_INTENT_PHRASES
      )) {
      return true;
    }

    if (this.looksLikeGraphAction(message, hiddenContext)) {
      return false;
    }
    if (this.looksLikeExistingExperimentActionRequest(message)) {
      return false;
    }

    if (this.containsAnySetupIntentTerm(
      message,
      SETUP_INTENT_WORDS,
      SETUP_INTENT_PHRASES
    )) {
      return true;
    }

    return this.isLikelyExperimentSetupRequest(message);
  }

  private looksLikeGraphAction(message: string, hiddenContext?: ChatHiddenContext): boolean {
    const graphContext = hiddenContext?.graphAgentContext;
    if (!graphContext?.thingieId) {
      return false;
    }
    return this.containsAnySetupIntentTerm(
      message,
      GRAPH_ACTION_SIGNAL_WORDS,
      GRAPH_ACTION_SIGNAL_PHRASES
    );
  }

  private looksLikeExistingExperimentActionRequest(message: string): boolean {
    return this.containsAnySetupIntentTerm(
      message,
      EXISTING_EXPERIMENT_ACTION_WORDS,
      EXISTING_EXPERIMENT_ACTION_PHRASES
    );
  }

  private containsAnySetupIntentTerm(
    message: string,
    words: readonly string[],
    phrases: readonly string[]
  ): boolean {
    const normalizedMessage = this.normalizeSetupIntentMessage(message);
    return words.some(word => normalizedMessage.includes(` ${word} `))
      || phrases.some(phrase => normalizedMessage.includes(` ${phrase} `));
  }

  private normalizeSetupIntentMessage(message: string): string {
    const normalizedCharacters: string[] = [];
    let previousCharacterWasSeparator = true;

    for (const character of message.toLowerCase()) {
      const isAlphaNumeric = (character >= 'a' && character <= 'z')
        || (character >= '0' && character <= '9');
      if (isAlphaNumeric) {
        normalizedCharacters.push(character);
        previousCharacterWasSeparator = false;
        continue;
      }
      if (!previousCharacterWasSeparator) {
        normalizedCharacters.push(' ');
        previousCharacterWasSeparator = true;
      }
    }

    const normalizedMessage = normalizedCharacters.join('').trim();
    return normalizedMessage.length > 0 ? ` ${normalizedMessage} ` : ' ';
  }

  private shouldBuildSetupContextForLlm(message: string, context: SetupCopilotContext, hiddenContext?: ChatHiddenContext): boolean {
    if (this.isSetupIntent(message, context, hiddenContext)) {
      return true;
    }

    return this.containsAnySetupIntentTerm(
      message,
      [
        'experiment',
        'experiments',
        'template',
        'templates',
        'project',
        'projects',
        'cgq',
        'mps',
        'lis',
        'bior',
        'microbial'
      ],
      [
        'cell culture',
        'cell growth quantifier',
        'shake flask'
      ]
    );
  }

  private hasTemplateRecommendationSignals(context: SetupCopilotContext): boolean {
    return !!context.organismClass
      || (context.measurementGoals?.length ?? 0) > 0
      || (context.requestedTemplateHints?.length ?? 0) > 0;
  }

  private inferTemplateOrganismClass(templateName: string): SetupOrganismClass | undefined {
    const chips = extractTemplateChipsFromName(templateName).chips.map(chip => chip.name.toLowerCase());
    if (chips.includes('microbial')) {
      return 'microbial';
    }
    if (chips.includes('cell culture')) {
      return 'cell_culture';
    }
    return this.inferOrganismClass(templateName);
  }

  private inferOrganismClass(value: string | undefined): SetupOrganismClass | undefined {
    if (!value) {
      return undefined;
    }
    if (MICROBIAL_PATTERNS.some(pattern => pattern.test(value))) {
      return 'microbial';
    }
    if (CELL_CULTURE_PATTERNS.some(pattern => pattern.test(value))) {
      return 'cell_culture';
    }
    return undefined;
  }

  private extractOrganismName(message: string): string | undefined {
    const namedOrganismPatterns = [
      /for\s+([a-z0-9 .-]+?)(?:\s+with|\s+using|\s+that|\s+in|\s*$)/i,
      /organism\s+([a-z0-9 .-]+?)(?:\s+with|\s+using|\s+that|\s+in|\s*$)/i
    ];
    for (const pattern of namedOrganismPatterns) {
      const match = message.match(pattern);
      if (match?.[1]) {
        const normalizedOrganismName = match[1]
          .trim()
          .replace(/^(?:my|the|an|a)\s+/i, '')
          .replace(/\s+experiment$/i, '')
          .trim();
        if (normalizedOrganismName) {
          return normalizedOrganismName;
        }
      }
    }
    return undefined;
  }

  private isLikelyExperimentSetupRequest(message: string): boolean {
    const normalizedMessage = this.normalizeSetupIntentMessage(message);
    if (!normalizedMessage.includes(' experiment ')
      && !normalizedMessage.includes(' experiments ')) {
      return false;
    }
    if (!this.containsAnySetupIntentTerm(
      message,
      SETUP_CREATE_REQUEST_WORDS,
      SETUP_CREATE_REQUEST_PHRASES
    )) {
      return false;
    }

    return (this.extractRequestedTemplateHints(message)?.length ?? 0) > 0
      || !!this.extractOrganismName(message)
      || !!this.inferOrganismClass(message)
      || this.extractReplicateCount(message) !== undefined
      || this.extractMeasurementGoals(message).some(goal => goal !== 'do');
  }

  private extractMeasurementGoals(message: string): SetupMeasurementGoal[] {
    const matches = MEASUREMENT_GOAL_PATTERNS
      .filter(entry => entry.patterns.some(pattern => pattern.test(message)))
      .map(entry => entry.goal);
    return [...new Set(matches)];
  }

  private extractRequestedTemplateHints(message: string): string[] | undefined {
    const matches = SETUP_TEMPLATE_HINT_PATTERNS
      .filter(entry => entry.messagePatterns.some(pattern => pattern.test(message)))
      .map(entry => entry.hint);
    return matches.length > 0 ? [...new Set(matches)] : undefined;
  }

  private extractReplicateCount(message: string): number | undefined {
    const match = message.match(/(\d+)\s+(?:replicates?|flasks?|cultures?)/i);
    if (!match?.[1]) {
      return undefined;
    }
    const parsedValue = Number.parseInt(match[1], 10);
    return this.normalizeReplicateCount(parsedValue);
  }

  private extractExperimentName(message: string): string | undefined {
    const quotedMatch = message.match(/(?:called|named)\s+"([^"]+)"/i);
    if (quotedMatch?.[1]) {
      return quotedMatch[1].trim();
    }

    const plainMatch = message.match(/(?:called|named)\s+([A-Za-z0-9 _-]+)/i);
    return plainMatch?.[1]?.trim();
  }

  private extractExperimentNameFromMessage(
    message: string,
    pendingQuestion?: SetupPendingQuestion
  ): string | undefined {
    const explicitExperimentName = this.extractExperimentName(message);
    if (explicitExperimentName) {
      return explicitExperimentName;
    }
    if (pendingQuestion !== 'experiment_name') {
      return undefined;
    }

    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      return undefined;
    }
    const quotedReplyMatch = trimmedMessage.match(/^"([^"]+)"$/);
    if (quotedReplyMatch?.[1]) {
      return quotedReplyMatch[1].trim();
    }
    return trimmedMessage;
  }

  private buildTemplateSearchText(templateMetadata: IApplicationTemplateMetadata): string {
    const parts = [
      templateMetadata.name,
      templateMetadata.descriptionTitle,
      templateMetadata.descriptionBodyHTML,
      ...templateMetadata.replicateMetadata
        .flatMap(replicate => replicate.processMetadata)
        .flatMap(process => process.taskTemplateMetadata)
        .flatMap(task => [
          task.name,
          task.descriptionTitle,
          task.descriptionBodyHTML,
          task.usedFor.join(' '),
          ...(task.deviceInputs?.map(input => input.deviceType) ?? [])
        ])
    ];
    return parts.join(' ').toLowerCase();
  }

  private extractTemplateGoals(searchableText: string): SetupMeasurementGoal[] {
    return MEASUREMENT_GOAL_PATTERNS
      .filter(entry => this.goalMatchesTemplate(entry.goal, searchableText))
      .map(entry => entry.goal);
  }

  private templateMatchesRequestedHint(searchableText: string, requestedHint: string): boolean {
    const hintDefinition = SETUP_TEMPLATE_HINT_PATTERNS.find(entry => entry.hint === requestedHint);
    if (!hintDefinition) {
      return false;
    }
    return hintDefinition.templatePatterns.some(pattern => this.matchesTextPattern(searchableText, pattern));
  }

  private matchesTextPattern(text: string, pattern: TemplateTextPattern): boolean {
    if (!pattern.wholeWord) {
      return text.includes(pattern.term);
    }
    let start = 0;
    while (start <= text.length - pattern.term.length) {
      const index = text.indexOf(pattern.term, start);
      if (index === -1) {
        return false;
      }
      const charBefore = index > 0 ? text.charCodeAt(index - 1) : -1;
      const charAfter = index + pattern.term.length < text.length ? text.charCodeAt(index + pattern.term.length) : -1;
      if (!this.isWordChar(charBefore) && !this.isWordChar(charAfter)) {
        return true;
      }
      start = index + 1;
    }
    return false;
  }

  private isWordChar(charCode: number): boolean {
    return (charCode >= 48 && charCode <= 57) ||
      (charCode >= 65 && charCode <= 90) ||
      (charCode >= 97 && charCode <= 122) ||
      charCode === 95;
  }

  private goalMatchesTemplate(goal: SetupMeasurementGoal, searchableText: string): boolean {
    const entry = MEASUREMENT_GOAL_PATTERNS.find(patterns => patterns.goal === goal);
    return entry?.patterns.some(pattern => pattern.test(searchableText)) ?? false;
  }

  private isPendingQuestionResolved(
    pendingQuestion: SetupPendingQuestion,
    context: SetupCopilotContext
  ): boolean {
    switch (pendingQuestion) {
      case 'organism_class':
        return !!context.organismClass;
      case 'measurement_goal':
        return (context.measurementGoals?.length ?? 0) > 0;
      case 'replicate_count':
        return !!context.replicateCount;
      case 'project':
        return !!context.projectId;
      case 'experiment_name':
        return !!context.experimentName?.trim();
    }
  }

  private formatGoal(goal: SetupMeasurementGoal): string {
    switch (goal) {
      case 'offline_biomass':
        return 'offline biomass';
      case 'otr':
        return 'OTR';
      case 'do':
        return 'DO';
      case 'mixed_monitoring':
        return 'mixed monitoring';
      default:
        return goal;
    }
  }

  private formatTemplateHint(hint: string): string {
    return SETUP_TEMPLATE_HINT_PATTERNS.find(entry => entry.hint === hint)?.displayName ?? hint;
  }

  private buildWizardFallbackAction(
    initialStep: WizardStep,
    context: SetupCopilotContext,
    recommendation: SetupTemplateRecommendation,
    explanation: string
  ): OpenWizardAtStepActionProposal | undefined {
    if (!recommendation.templateId) {
      return undefined;
    }

    return {
      action: 'openWizardAtStep',
      explanation,
      initialStep,
      prefill: {
        templateId: recommendation.templateId,
        templateName: recommendation.templateName,
        experimentName: context.experimentName,
        projectId: context.projectId,
        projectTitle: context.projectTitle,
        replicateCount: context.replicateCount,
        organismName: context.organismName,
        organismClass: context.organismClass,
        measurementGoals: context.measurementGoals,
        perReplicateOverridesRequested: context.perReplicateOverridesRequested
      }
    };
  }

  private syncTaskConfigurationSession(taskConfigurationSession: SetupCopilotTaskConfigurationSession): void {
    this.thingieCreationHelper.preProcessConditionalOutputsAndInputs(taskConfigurationSession.templateThingie);
    this.applyTemplateTasksToThingies(taskConfigurationSession);
    taskConfigurationSession.thingies.forEach(thingie => {
      this.thingieCreationHelper.preProcessConditionalOutputsAndInputs(thingie);
    });
    taskConfigurationSession.blockingTasks = this.collectBlockingTasks(taskConfigurationSession.templateThingie);
    if (!taskConfigurationSession.focusedTask && taskConfigurationSession.blockingTasks.length > 0) {
      taskConfigurationSession.focusedTask = taskConfigurationSession.blockingTasks[0];
      return;
    }
    if (taskConfigurationSession.focusedTask) {
      taskConfigurationSession.focusedTask = this.buildBlockingTask(
        taskConfigurationSession.templateThingie,
        taskConfigurationSession.focusedTask.processIdx,
        taskConfigurationSession.focusedTask.taskIdx
      );
    }
  }

  private applyTemplateTasksToThingies(taskConfigurationSession: SetupCopilotTaskConfigurationSession): void {
    taskConfigurationSession.thingies.forEach(thingie => {
      thingie.baseName = taskConfigurationSession.templateThingie.baseName;
      thingie.project = taskConfigurationSession.templateThingie.project;
      thingie.createdFromTemplateId = taskConfigurationSession.templateThingie.createdFromTemplateId;
      taskConfigurationSession.templateThingie.processes.forEach((templateProcess, processIdx) => {
        const thingieProcess = thingie.processes[processIdx];
        if (!thingieProcess) {
          return;
        }
        thingieProcess.tasks = deepCopySync(templateProcess.tasks);
      });
    });
  }

  private collectBlockingTasks(templateThingie: IThingie): SetupCopilotBlockingTask[] {
    return templateThingie.processes.flatMap((process, processIdx) => process.tasks.flatMap(
      (task, taskIdx) => ExperimentCreationValidator.validateTask(task)
        ? []
        : [this.buildBlockingTask(templateThingie, processIdx, taskIdx)]
    ));
  }

  private buildBlockingTask(
    templateThingie: IThingie,
    processIdx: number,
    taskIdx: number
  ): SetupCopilotBlockingTask {
    const process = templateThingie.processes[processIdx];
    const task = process?.tasks[taskIdx];
    const missingInputLabels = task
      ? this.collectMissingInputLabels(task)
      : [];
    return {
      processIdx,
      taskIdx,
      processName: process?.name,
      taskName: task?.name || `Task ${taskIdx + 1}`,
      missingInputLabels
    };
  }

  private collectMissingInputLabels(task: ITask): string[] {
    const missingInputLabels = Object.values(task.inputs)
      .flatMap(input => this.isInputBlockingTaskConfiguration(input, task)
        ? [this.getTaskInputLabel(input)]
        : []);
    return [...new Set(missingInputLabels)];
  }

  private isInputBlockingTaskConfiguration(input: ITaskInputType, task: ITask): boolean {
    const condition = 'condition' in input ? input.condition : undefined;
    if (condition) {
      try {
        if (!ExperimentCreationValidator.validateTaskInputCondition(condition, task)) {
          return false;
        }
      } catch {
        return true;
      }
    }

    if ('value' in input) {
      return ('optional' in input ? input.optional !== true : true)
        && (input.value === undefined || input.value === null || input.value === '');
    }

    if (input.source === 'reassignable') {
      const reassignableInput = input as IReassignable;
      return reassignableInput.optional !== true && reassignableInput.value === undefined;
    }

    return false;
  }

  private getTaskInputLabel(input: ITaskInputType): string {
    if ('name' in input && input.name) {
      return input.name;
    }
    if ('item' in input) {
      return input.item.valueInfo[0]?.name ?? 'Unnamed input';
    }
    return 'Unnamed input';
  }

  private resolveNextFocusedTask(
    taskConfigurationSession: SetupCopilotTaskConfigurationSession
  ): SetupCopilotBlockingTask | undefined {
    if (taskConfigurationSession.blockingTasks.length === 0) {
      return undefined;
    }

    const focusedTask = taskConfigurationSession.focusedTask;
    if (!focusedTask) {
      return taskConfigurationSession.blockingTasks[0];
    }

    const currentBlockingIndex = taskConfigurationSession.blockingTasks.findIndex(
      task => task.processIdx === focusedTask.processIdx && task.taskIdx === focusedTask.taskIdx
    );
    if (currentBlockingIndex >= 0) {
      return taskConfigurationSession.blockingTasks[currentBlockingIndex];
    }

    return taskConfigurationSession.blockingTasks.find(
      task => task.processIdx > focusedTask.processIdx
        || (task.processIdx === focusedTask.processIdx && task.taskIdx > focusedTask.taskIdx)
    ) ?? taskConfigurationSession.blockingTasks[0];
  }

  private buildTaskConfigurationMessage(taskConfigurationSession: SetupCopilotTaskConfigurationSession): string {
    const focusedTask = taskConfigurationSession.focusedTask ?? taskConfigurationSession.blockingTasks[0];
    if (!focusedTask) {
      return 'I still need task configuration before I can create this experiment.';
    }

    const missingInputText = focusedTask.missingInputLabels.length > 0
      ? focusedTask.missingInputLabels.join(', ')
      : 'Please complete the required task inputs below.';
    return [
      'I still need required task configuration before I can create this experiment.',
      '',
      `- Task: \`${focusedTask.taskName}\``,
      `- Process: \`${focusedTask.processName || `Process ${focusedTask.processIdx + 1}`}\``,
      `- Remaining blocking tasks: \`${taskConfigurationSession.blockingTasks.length}\``,
      `- Required inputs: ${missingInputText}`
    ].join('\n');
  }

  private buildCreateExperimentActionProposal(draft: SetupExperimentDraft): ActionProposal {
    return {
      action: 'createExperimentFromChat',
      explanation: `Create ${draft.replicateCount} replicate(s) from ${draft.templateName} in ${draft.projectTitle}.`,
      draft
    };
  }

  private normalizeMeasurementGoals(goals: string[] | undefined): SetupMeasurementGoal[] {
    const normalizedGoals = (goals ?? [])
      .map(goal => goal.trim())
      .filter((goal): goal is SetupMeasurementGoal => MEASUREMENT_GOAL_PATTERNS.some(entry => entry.goal === goal));
    return [...new Set(normalizedGoals)];
  }

  private normalizeReplicateCount(replicateCount: number | undefined): number | undefined {
    if (replicateCount === undefined || !Number.isFinite(replicateCount)) {
      return undefined;
    }
    if (!Number.isInteger(replicateCount)) {
      return undefined;
    }
    if (replicateCount < 1 || replicateCount > MAX_REPLICATE_COUNT) {
      return undefined;
    }
    return replicateCount;
  }

  private async validateDraftForCreation(
    draft: SetupExperimentDraft
  ): Promise<{ ok: true; draft: SetupExperimentDraft } | { ok: false; errors: string[] }> {
    const normalizedExperimentName = draft.experimentName.trim();
    if (!normalizedExperimentName) {
      return { ok: false, errors: ['Experiment name is required.'] };
    }

    const normalizedReplicateCount = this.normalizeReplicateCount(draft.replicateCount);
    if (!normalizedReplicateCount) {
      return { ok: false, errors: [`Replicate count must be between 1 and ${MAX_REPLICATE_COUNT}.`] };
    }

    const accessibleProjects = await this.getAccessibleEditorProjects();
    const matchingProject = accessibleProjects.find(project => project._id === draft.projectId.trim());
    if (!matchingProject) {
      return { ok: false, errors: ['You need editor access to the selected project before creating the experiment.'] };
    }

    const normalizedDraft: SetupExperimentDraft = {
      ...draft,
      templateId: draft.templateId.trim(),
      templateName: draft.templateName.trim(),
      experimentName: normalizedExperimentName,
      projectId: matchingProject._id,
      projectTitle: matchingProject.title,
      replicateCount: normalizedReplicateCount,
      organismName: draft.organismName?.trim() || undefined,
      measurementGoals: this.normalizeMeasurementGoals(draft.measurementGoals),
      requiredDeviceTypes: (draft.requiredDeviceTypes ?? []).map(deviceType => deviceType.trim()).filter(Boolean)
    };

    const takenExperimentNames = await lastValueFrom(this.thingieService.checkForTakenNames([
      { name: normalizedDraft.experimentName }
    ]));
    if (takenExperimentNames.includes(normalizedDraft.experimentName)) {
      return { ok: false, errors: [`Experiment name "${normalizedDraft.experimentName}" is already taken.`] };
    }

    const draftThingieNames = this.buildReplicateNames(normalizedDraft.replicateCount);
    const takenThingieNames = await lastValueFrom(this.thingieService.checkForTakenThingieNames(
      draftThingieNames.map(name => ({ name }))
    ));
    if (takenThingieNames.length > 0) {
      return {
        ok: false,
        errors: [`Replicate names are already taken: ${takenThingieNames.join(', ')}.`]
      };
    }

    return {
      ok: true,
      draft: normalizedDraft
    };
  }

  private async resolveTeamIdForProject(projectId: string): Promise<string> {
    if (!projectId) {
      return '';
    }
    const accessibleProjects = await this.getAccessibleEditorProjects();
    return accessibleProjects.find(project => project._id === projectId)?.teamID ?? '';
  }

  private buildReplicateNames(replicateCount: number): string[] {
    const digitCount = String(replicateCount).length;
    return Array.from({ length: replicateCount }, (_, index) => `Replicate ${String(index + 1).padStart(digitCount, '0')}`);
  }
}
